import { useCallback, useEffect } from 'react'
import { z } from 'zod'
import { useStateStorage, StorageKeys } from '@reward-platform/utils'
import { getOpCoMetaConfig } from '@reward-platform/platform-config'
import { getClientRequestHeader, setClientRequestHeader } from '~/services/clients/nextApiClient'
import { UserValue } from './User.types'
import { useAuthContext } from '../auth'
import { usePartner } from '../partner'

const INVALID_COUNTRY_CODE = 'XX'

const parseString = (value: unknown, defaultValue: string) => {
  const parsedString = z.string().safeParse(value)

  if (!parsedString.success) {
    return defaultValue
  }

  return parsedString.data
}

const getCountryCode = (user: UserValue, defaultCountryCode: string) => {
  const countryFromCountryOfResidence =
    user.person?.countryOfResidence?.identifier !== INVALID_COUNTRY_CODE
      ? user.person?.countryOfResidence?.identifier
      : undefined

  const countryFromPostalAddresses = user.person?.postalAddresses
    ?.map(({ country }) => country)
    ?.find((country) => country !== INVALID_COUNTRY_CODE)

  return countryFromCountryOfResidence ?? countryFromPostalAddresses ?? defaultCountryCode
}

const useUserHeaders = (user?: UserValue) => {
  const { isAuthenticated, isLoading } = useAuthContext()
  const { code: opCo } = usePartner()
  const { defaultCountryCode, defaultCurrencyCode } = getOpCoMetaConfig(opCo)
  const { storage: countryCodeState, setStorage: setCountryCodeState } = useStateStorage({
    key: StorageKeys.USER_COUNTRY_CODE,
    parser: (value) => parseString(value, defaultCountryCode),
  })
  const { storage: currencyCodeState, setStorage: setCurrencyCodeState } = useStateStorage({
    key: StorageKeys.USER_CURRENCY_CODE,
    parser: (value) => parseString(value, defaultCurrencyCode),
  })

  const resetStorage = useCallback(() => {
    setCountryCodeState(defaultCountryCode)
    setCurrencyCodeState(defaultCurrencyCode)
  }, [defaultCountryCode, defaultCurrencyCode, setCountryCodeState, setCurrencyCodeState])

  useEffect(() => {
    if (!isAuthenticated && !isLoading) {
      resetStorage()
    }
  }, [isAuthenticated, isLoading, resetStorage])

  useEffect(() => {
    if (!user || !isAuthenticated) {
      return
    }

    const userCountryCode = getCountryCode(user, defaultCountryCode)
    setCountryCodeState(userCountryCode)

    const userCurrencyCode = user.person?.currencyCode
    setCurrencyCodeState(userCurrencyCode)
  }, [defaultCountryCode, isAuthenticated, setCountryCodeState, setCurrencyCodeState, user])

  useEffect(() => {
    // TODO: Move to including headers in requests directly as this doesn't always run on time, see RPS-13458
    if (!!countryCodeState && countryCodeState !== getClientRequestHeader('countryCode')) {
      setClientRequestHeader('countryCode', countryCodeState)
    }
    if (!!currencyCodeState && currencyCodeState !== getClientRequestHeader('currencyCode')) {
      setClientRequestHeader('currencyCode', currencyCodeState)
    }
  }, [countryCodeState, currencyCodeState])

  return { countryCode: countryCodeState, currencyCode: currencyCodeState }
}

export default useUserHeaders
